import { showToast } from "../common";
import { postFormData } from "../api";
import { redactStudentNameMasks } from "./studentNameMasks";

const DEFAULT_SCREENSHOT_STATUS = "Screenshot nie został jeszcze dodany.";
const MAX_SCREENSHOT_WIDTH = 1600;
const FALLBACK_SCREENSHOT_WIDTH = 1280;

function resolveHtml2Canvas(moduleValue) {
  const candidates = [
    moduleValue,
    moduleValue?.default,
    moduleValue?.html2canvas,
    moduleValue?.default?.default,
    moduleValue?.default?.html2canvas,
  ];

  const html2canvas = candidates.find((candidate) => typeof candidate === "function");
  if (typeof html2canvas !== "function") {
    throw new Error("Biblioteka screenshotów nie została załadowana poprawnie.");
  }

  return html2canvas;
}

function getWidgets(root = document) {
  if (root instanceof Element && root.matches("[data-report-problem-widget]")) {
    return [root];
  }

  if (!(root instanceof Document || root instanceof Element)) {
    return [];
  }

  return Array.from(root.querySelectorAll("[data-report-problem-widget]"));
}

function setHiddenValue(form, name, value) {
  const field = form.querySelector(`[name="${CSS.escape(name)}"]`);
  if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
    field.value = String(value ?? "");
  }
}

function populateClientContext(widget, form) {
  setHiddenValue(form, "page_url", window.location.href);
  setHiddenValue(form, "page_title", document.title || "");
  setHiddenValue(form, "current_view", widget.dataset.reportProblemCurrentView || document.title || "");
  setHiddenValue(form, "screen_resolution", `${window.screen?.width || 0}x${window.screen?.height || 0}`);
  setHiddenValue(form, "viewport_size", `${window.innerWidth || 0}x${window.innerHeight || 0}`);
  setHiddenValue(form, "screen_orientation", window.screen?.orientation?.type || "");
  setHiddenValue(form, "device_pixel_ratio", String(window.devicePixelRatio || 1));
  setHiddenValue(form, "language", navigator.language || "");
  setHiddenValue(form, "timezone", Intl.DateTimeFormat().resolvedOptions().timeZone || "");
  setHiddenValue(form, "platform", navigator.userAgentData?.platform || navigator.platform || "");
  setHiddenValue(form, "referrer", document.referrer || "");
  setHiddenValue(form, "captured_at", new Date().toISOString());
  setHiddenValue(form, "client_user_agent", navigator.userAgent || "");
}

function setPanelOpen(widget, isOpen) {
  const panel = widget.querySelector("[data-report-problem-panel]");
  const toggle = widget.querySelector("[data-report-problem-toggle]");
  if (!(panel instanceof HTMLElement) || !(toggle instanceof HTMLElement)) {
    return;
  }

  panel.hidden = !isOpen;
  widget.classList.toggle("is-open", isOpen);
  toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");

  if (isOpen) {
    const description = widget.querySelector("textarea[name='description']");
    if (description instanceof HTMLTextAreaElement) {
      window.setTimeout(() => {
        description.focus();
      }, 60);
    }
  }
}

function setMessage(target, message = "") {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  target.textContent = message;
  target.classList.toggle("d-none", message.trim() === "");
}

function updateScreenshotStatus(widget, message, isError = false) {
  const status = widget.querySelector("[data-report-problem-screenshot-status]");
  if (!(status instanceof HTMLElement)) {
    return;
  }

  status.textContent = message;
  status.classList.toggle("text-danger", isError);
  status.classList.toggle("text-muted", !isError);
}

function resizeCanvas(canvas, maxWidth) {
  if (!(canvas instanceof HTMLCanvasElement) || canvas.width <= maxWidth) {
    return canvas;
  }

  const scale = maxWidth / canvas.width;
  const resizedCanvas = document.createElement("canvas");
  resizedCanvas.width = Math.round(canvas.width * scale);
  resizedCanvas.height = Math.round(canvas.height * scale);

  const context = resizedCanvas.getContext("2d");
  if (!context) {
    return canvas;
  }

  context.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
  return resizedCanvas;
}

function canvasToBlob(canvas, quality = 0.82) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("Nie udało się przygotować zrzutu ekranu."));
      },
      "image/jpeg",
      quality,
    );
  });
}

async function buildScreenshotFile(canvas) {
  let blob = await canvasToBlob(resizeCanvas(canvas, MAX_SCREENSHOT_WIDTH), 0.82);
  if (blob.size > 2_500_000) {
    blob = await canvasToBlob(resizeCanvas(canvas, FALLBACK_SCREENSHOT_WIDTH), 0.68);
  }

  return new File([blob], `problem-report-${new Date().toISOString().replaceAll(/[:.]/g, "-")}.jpg`, {
    type: blob.type || "image/jpeg",
  });
}

async function captureScreenshot(widget) {
  widget.classList.add("is-capturing");

  await new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

  try {
    const html2canvasModule = await import("html2canvas");
    const html2canvas = resolveHtml2Canvas(html2canvasModule);
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      backgroundColor: "#eef2f7",
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      onclone: redactStudentNameMasks,
    });

    return await buildScreenshotFile(canvas);
  } finally {
    widget.classList.remove("is-capturing");
  }
}

function setBusyState(widget, isBusy) {
  const submitButton = widget.querySelector("[data-report-problem-submit]");
  const captureButton = widget.querySelector("[data-report-problem-capture]");

  if (submitButton instanceof HTMLButtonElement) {
    submitButton.disabled = isBusy;
  }

  if (captureButton instanceof HTMLButtonElement) {
    captureButton.disabled = isBusy;
  }
}

function bindWidget(widget) {
  if (widget.dataset.reportProblemWidgetInitialized === "1") {
    return;
  }

  const form = widget.querySelector("[data-report-problem-form]");
  const toggle = widget.querySelector("[data-report-problem-toggle]");
  const closeButtons = widget.querySelectorAll("[data-report-problem-close]");
  const captureButton = widget.querySelector("[data-report-problem-capture]");
  const errorBox = widget.querySelector("[data-report-problem-error]");
  const successBox = widget.querySelector("[data-report-problem-success]");

  if (!(form instanceof HTMLFormElement) || !(toggle instanceof HTMLButtonElement)) {
    return;
  }

  widget.dataset.reportProblemWidgetInitialized = "1";
  // Allow reporting while a Bootstrap modal traps focus on the rest of the page.
  widget.addEventListener("focusin", (event) => event.stopPropagation());
  let screenshotFile = null;

  populateClientContext(widget, form);
  updateScreenshotStatus(widget, DEFAULT_SCREENSHOT_STATUS);

  toggle.addEventListener("click", () => {
    populateClientContext(widget, form);
    setPanelOpen(widget, !widget.classList.contains("is-open"));
  });

  closeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      setPanelOpen(widget, false);
    });
  });

  if (captureButton instanceof HTMLButtonElement) {
    captureButton.addEventListener("click", async () => {
      setMessage(errorBox, "");
      setMessage(successBox, "");
      updateScreenshotStatus(widget, "Trwa przygotowanie zrzutu ekranu...");
      setBusyState(widget, true);

      try {
        screenshotFile = await captureScreenshot(widget);
        updateScreenshotStatus(widget, `Dodano zrzut ekranu (${Math.round(screenshotFile.size / 1024)} KB).`);
        showToast("Dodano zrzut ekranu do zgłoszenia.", { type: "success" });
      } catch (error) {
        screenshotFile = null;
        updateScreenshotStatus(widget, "Nie udało się przygotować zrzutu ekranu. Zgłoszenie nadal można wysłać bez obrazu.", true);
        showToast(String(error?.message || "Nie udało się przygotować zrzutu ekranu."), { type: "warning" });
      } finally {
        setBusyState(widget, false);
      }
    });
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    setMessage(errorBox, "");
    setMessage(successBox, "");
    populateClientContext(widget, form);

    const formData = new FormData(form);
    if (screenshotFile instanceof File) {
      formData.set("screenshot", screenshotFile, screenshotFile.name);
    }

    setBusyState(widget, true);

    try {
      const payload = await postFormData(widget.dataset.reportProblemApiUrl || "/api/report-problem", formData, formData.get("_csrf") || "");
      const successMessage = String(payload?.message || payload?.toast?.message || "Zgłoszenie zostało wysłane.");

      form.reset();
      screenshotFile = null;
      populateClientContext(widget, form);
      updateScreenshotStatus(widget, DEFAULT_SCREENSHOT_STATUS);
      setMessage(successBox, successMessage);
      const detailLink = widget.querySelector('[data-report-problem-detail]');
      if (detailLink instanceof HTMLAnchorElement && payload?.report_url) {
        detailLink.href = payload.report_url;
        detailLink.textContent = `Zobacz zgłoszenie #${payload.report_id}`;
        detailLink.hidden = false;
      }
      showToast(successMessage, { type: "success" });
    } catch (error) {
      const errorMessage = String(error?.message || "Nie udało się wysłać zgłoszenia.");
      setMessage(errorBox, errorMessage);
      showToast(errorMessage, { type: "danger" });
    } finally {
      setBusyState(widget, false);
    }
  });
}

export function initReportProblemWidget(root = document) {
  getWidgets(root).forEach(bindWidget);
}
